import type { ResolutionContext } from 'inversify';

import { container as containerDefault } from '@vk-hr-tek/core/ioc';
import { RoleService, TokenService, LocaleService } from '@vk-hr-tek/core/http';
import { ListService } from '@vk-hr-tek/core/filter/types';
import {
  GroupingStrategy,
  NotificationsMapper,
  NotificationsEventEmitter,
} from '@vk-hr-tek/core/notifications/types';
import { Notification } from '@vk-hr-tek/core/notifications/gen/notifications';
import {
  Logger,
  ConsoleLogger,
  SentryLogger,
  User,
} from '@vk-hr-tek/core/logger';
import { History } from '@vk-hr-tek/core/history';
import {
  LocalStorage,
  LocalStorageService,
} from '@vk-hr-tek/core/local-storage';
import { UpdatorService } from '@vk-hr-tek/core/updator';
import { DownloadNotificationEventEmitter } from '@vk-hr-tek/core/download-notification/types';
import { TranslationLocaleService } from '@vk-hr-tek/core/translations/services';
import { Download, DownloadService } from '@vk-hr-tek/core/download';

import { DashboardRouter } from '../dashboard/types';
import { AbsencesRouter } from '../absences/types';
import { AuthTokenService, AuthOpenIdService } from '../auth/services';
import { CandidateRouter } from '../candidate/types';
import { CandidatesRouter } from '../candidates/types';
import { EmployeesRouter } from '../employees/types';
import { EventsRouter, EventsFlowMapper } from '../events/types';
import {
  EventsWorkerFlowMapper,
  EventsThreadFlowMapper,
} from '../events/mappers/flow';
import { FiltersPresetsRouter } from '../filtersPreset/types';
import { EventNotificationsMapper } from '../events/mappers';
import { PolicyNotificationsMapper } from '../policy/mappers';
import {
  EventsListService,
  EventNotificationService,
} from '../events/services';
import { OrganizationRouter } from '../organization/types';
import { PolicyRouter } from '../policy/types';
import { RedirectPage } from '../auth/types';
import {
  SettingsRouter,
  SettingsAttorneysRouter,
  SettingsAttorneysUsersRouter,
  SettingsGroupsRouter,
  SettingsCompanyRouter,
  SettingsEventTypesRouter,
  SettingsSubstitutesRouter,
  SettingsTemplatesRouter,
  SettingsVacationsRouter,
  SettingsCompanySignTypesRouter,
} from '../settings/types';
import { UserRoleService, UserEntityService } from '../user/services';
import { UserRouter } from '../user/types';
import { VacationsRouter } from '../vacations/types';
import { CompetenciesRouter } from '../competencies/types';
import { PersonalRouter } from '../personal/types';
import { NewsRouter } from '../news/types';
import { QuizRouter } from '../quiz/types';
import { BellNotificationsRouter } from '../bell-notifications/types';
import { SocialAccessRouter } from '../social-access/types';
import { GeneralNewsRouter } from '../general-news/types';
import { RedirectPageService } from '../auth/services/common';
import { ReviewRouter } from '../review/types';

import {
  AbsencesRouterService,
  DashboardRouterService,
  CandidatesRouterService,
  CandidateRouterService,
  EmployeesRouterService,
  EventsRouterService,
  FiltersPresetsRouterService,
  OrganizationRouterService,
  PolicyRouterService,
  RouterServices,
  SettingsAttorneysRouterService,
  SettingsAttorneysUsersRouterService,
  SettingsCompanySignTypesRouterService,
  SettingsCompanyRouterService,
  SettingsEventTypesRouterService,
  SettingsGroupsRouterService,
  SettingsRouterService,
  SettingsSubstitutesRouterService,
  SettingsTemplatesRouterService,
  SettingsVacationsRouterService,
  UserRouterService,
  VacationsRouterService,
  CompetenciesRouterService,
  PersonalRouterService,
  QuizRouterService,
  NewsRouterService,
  GeneralNewsRouterService,
  BellNotificationsRouterService,
  SocialAccessRouterService,
  ReviewRouterService,
} from './router';

const emptyNotificationsEmitter: NotificationsEventEmitter = {
  onShow: () => {},
};

const emptyDownloadNotificationsEmitter: DownloadNotificationEventEmitter = {
  onShow: () => {},
  onCancel: () => {},
};

interface InitOptions {
  history: History;
  router?: RouterServices;
  notifications?: NotificationsEventEmitter;
  downloadNotifications?: DownloadNotificationEventEmitter;
  localStorageNamespace?: string;
  role?: RoleService;
  locale?: LocaleService;
  redirectPageNamespace?: string;
  download?: Download;
  auth?: TokenService;
  updator?: UpdatorService;
  container?: typeof containerDefault;
  flowWidget?: boolean;
}

export const init = ({
  container = containerDefault,
  router,
  notifications = emptyNotificationsEmitter,
  downloadNotifications = emptyDownloadNotificationsEmitter,
  history,
  role,
  locale,
  redirectPageNamespace = '',
  auth,
  localStorageNamespace = '',
  download,
  updator,
  flowWidget,
}: InitOptions) => {
  const createMapper = (mappers: NotificationsMapper[]) => {
    return {
      mapNotifications(data: Notification[]) {
        return mappers.flatMap((mapper: NotificationsMapper) =>
          mapper.mapNotifications(data),
        );
      },
    };
  };

  container.unbindAll();
  container.bind<History>(History).toConstantValue(history);
  container
    .bind<LocalStorage>(LocalStorage)
    .toConstantValue(new LocalStorageService(localStorageNamespace));

  container.bind<ListService>(ListService).to(EventsListService);

  if (flowWidget) {
    container
      .bind<EventsFlowMapper>(EventsFlowMapper)
      .to(EventsThreadFlowMapper);
  } else {
    container
      .bind<EventsFlowMapper>(EventsFlowMapper)
      .to(EventsWorkerFlowMapper);
  }

  if (role) {
    container.bind<RoleService>(RoleService).toConstantValue(role);
  } else {
    container.bind<RoleService>(RoleService).to(UserRoleService);
  }

  container
    .bind<RedirectPage>(RedirectPage)
    .toConstantValue(new RedirectPageService(container, redirectPageNamespace));

  container.bind<TokenService>(TokenService).to(AuthTokenService);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS2774
  if (auth && auth.get && auth.restore) {
    container.bind<AuthOpenIdService>(AuthOpenIdService).toConstantValue(auth);
  }

  container.bind<User>(User).to(UserEntityService);

  if (locale) {
    container.bind<LocaleService>(LocaleService).toConstantValue(locale);
  } else {
    container.bind<LocaleService>(LocaleService).to(TranslationLocaleService);
  }

  if (download) {
    container.bind<Download>(Download).toConstantValue(download);
  } else {
    container.bind<Download>(Download).to(DownloadService);
  }

  container
    .bind<Logger>(Logger)
    .to(window.REACT_APP_VKHRTEK_SENTRY_URL ? SentryLogger : ConsoleLogger);

  if (updator) {
    container.bind<UpdatorService>(UpdatorService).toConstantValue(updator);
  } else {
    container.bind<UpdatorService>(UpdatorService).to(UpdatorService);
  }

  container
    .bind<GroupingStrategy>(GroupingStrategy)
    .to(EventNotificationService);
  container
    .bind<NotificationsMapper>(NotificationsMapper)
    .toDynamicValue((context) => {
      const roleService = context.get<RoleService>(RoleService);
      return createMapper([
        new EventNotificationsMapper(roleService),
        new PolicyNotificationsMapper(),
      ]);
    });

  container
    .bind<NotificationsEventEmitter>(NotificationsEventEmitter)
    .toConstantValue({
      ...emptyNotificationsEmitter,
      ...('onShow' in notifications ? notifications : {}),
    });

  container
    .bind<DownloadNotificationEventEmitter>(DownloadNotificationEventEmitter)
    .toConstantValue({
      ...emptyDownloadNotificationsEmitter,
      ...('onShow' in downloadNotifications ? downloadNotifications : {}),
    });

  type Routers = Required<RouterServices>[keyof Required<RouterServices>];

  const createProxyRouter = <T extends Routers>(
    context: ResolutionContext,
    constructor: new (historyService: History, roleService: RoleService) => T,
    key: keyof RouterServices,
  ) => {
    const routerImplementation = new constructor(
      context.get<History>(History),
      context.get<RoleService>(RoleService),
    );

    return new Proxy(routerImplementation, {
      get(target, method: keyof Routers) {
        if (router && router[key] && method in (router as any)[key]) {
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          return function (...args: any[]) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            router[key]?.[method](...args);

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            target[method](...args);
          };
        } else {
          return target[method];
        }
      },
    });
  };

  container
    .bind<CandidatesRouter>(CandidatesRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(context, CandidatesRouterService, 'candidates');
    });

  container.bind<CandidateRouter>(CandidateRouter).to(CandidateRouterService);

  container.bind<EmployeesRouter>(EmployeesRouter).toDynamicValue((context) => {
    return createProxyRouter(context, EmployeesRouterService, 'employees');
  });

  container.bind<DashboardRouter>(DashboardRouter).toDynamicValue((context) => {
    return createProxyRouter(context, DashboardRouterService, 'dashboard');
  });

  container.bind<AbsencesRouter>(AbsencesRouter).toDynamicValue((context) => {
    return createProxyRouter(context, AbsencesRouterService, 'absences');
  });

  container.bind<EventsRouter>(EventsRouter).toDynamicValue((context) => {
    return createProxyRouter(context, EventsRouterService, 'events');
  });

  container
    .bind<FiltersPresetsRouter>(FiltersPresetsRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        FiltersPresetsRouterService,
        'filtersPresets',
      );
    });

  container
    .bind<CompetenciesRouter>(CompetenciesRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(context, CompetenciesRouterService, 'events');
    });

  container
    .bind<OrganizationRouter>(OrganizationRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        OrganizationRouterService,
        'organization',
      );
    });

  container.bind<PolicyRouter>(PolicyRouter).toDynamicValue((context) => {
    return createProxyRouter(context, PolicyRouterService, 'policy');
  });

  container
    .bind<SettingsAttorneysRouter>(SettingsAttorneysRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsAttorneysRouterService,
        'attorneysSettings',
      );
    });

  container
    .bind<SettingsAttorneysUsersRouter>(SettingsAttorneysUsersRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsAttorneysUsersRouterService,
        'attorneysUsersSettings',
      );
    });

  container
    .bind<SettingsCompanySignTypesRouter>(SettingsCompanySignTypesRouter)
    .toDynamicValue((context) => {
      const settingsRouter = new SettingsCompanySignTypesRouterService(
        context.get<History>(History),
        context.get<RoleService>(RoleService),
      );
      return new Proxy(settingsRouter, {
        get(target, method: keyof SettingsCompanySignTypesRouter) {
          if (
            router?.companySignTypeSettings &&
            method in router.companySignTypeSettings
          ) {
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            return function (...args: any[]) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              router.companyUnepTypeSettings?.[method](...args);

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              target[method](...args);
            };
          } else {
            return target[method];
          }
        },
      });
    });

  container
    .bind<SettingsEventTypesRouter>(SettingsEventTypesRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsEventTypesRouterService,
        'eventTypesSettings',
      );
    });

  container
    .bind<SettingsGroupsRouter>(SettingsGroupsRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsGroupsRouterService,
        'groupsSettings',
      );
    });

  container.bind<SettingsRouter>(SettingsRouter).toDynamicValue((context) => {
    return createProxyRouter(context, SettingsRouterService, 'settings');
  });

  container
    .bind<SettingsSubstitutesRouter>(SettingsSubstitutesRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsSubstitutesRouterService,
        'substitutesSettings',
      );
    });

  container
    .bind<SettingsTemplatesRouter>(SettingsTemplatesRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsTemplatesRouterService,
        'templatesSettings',
      );
    });

  container.bind<QuizRouter>(QuizRouter).toDynamicValue((context) => {
    return createProxyRouter(context, QuizRouterService, 'quiz');
  });

  container
    .bind<SocialAccessRouter>(SocialAccessRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SocialAccessRouterService,
        'templatesSettings',
      );
    });

  container
    .bind<SettingsVacationsRouter>(SettingsVacationsRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        SettingsVacationsRouterService,
        'vacationsSettings',
      );
    });

  container.bind<UserRouter>(UserRouter).toDynamicValue((context) => {
    return createProxyRouter(context, UserRouterService, 'user');
  });

  container.bind<VacationsRouter>(VacationsRouter).toDynamicValue((context) => {
    return createProxyRouter(context, VacationsRouterService, 'vacations');
  });

  container.bind<PersonalRouter>(PersonalRouter).toDynamicValue((context) => {
    return createProxyRouter(context, PersonalRouterService, 'personal');
  });

  container
    .bind<SettingsCompanyRouter>(SettingsCompanyRouter)
    .toDynamicValue((context) => {
      const settingsCompanyRouter = new SettingsCompanyRouterService(
        context.get<History>(History),
      );
      return new Proxy(settingsCompanyRouter, {
        get(target, method: keyof SettingsCompanyRouter) {
          if (router?.vacations && method in router.vacations) {
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            return function (...args: any[]) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              router.vacations?.[method](...args);

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              target[method](...args);
            };
          } else {
            return target[method];
          }
        },
      });
    });

  container.bind<NewsRouter>(NewsRouter).toDynamicValue((context) => {
    return createProxyRouter(context, NewsRouterService, 'news');
  });

  container
    .bind<GeneralNewsRouter>(GeneralNewsRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        GeneralNewsRouterService,
        'generalNews',
      );
    });

  container
    .bind<BellNotificationsRouter>(BellNotificationsRouter)
    .toDynamicValue((context) => {
      return createProxyRouter(
        context,
        BellNotificationsRouterService,
        'bellNotifications',
      );
    });

  container.bind<ReviewRouter>(ReviewRouter).toDynamicValue((context) => {
    return createProxyRouter(context, ReviewRouterService, 'review');
  });
};
